import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gmailApi } from '../services/api';
import {
  ApiResponse,
  GmailLabelsResponse,
  GmailMessagesResponse,
  GmailMessage,
  GmailSendMessageResponse,
  GmailSendMessageRequest
} from '../types/api';
import toast from 'react-hot-toast';

type ApiErrorResponse = {
  response?: {
    data?: {
      message?: string;
    };
  };
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null) {
    const typed = error as ApiErrorResponse;
    return typed.response?.data?.message ?? fallback;
  }
  return fallback;
};

// Query keys
const scopeKey = (userId?: string) => userId ?? 'default';

export const gmailQueryKeys = {
  all: (userId?: string) => ['gmail', scopeKey(userId)] as const,
  labels: (userId?: string) => ['gmail', 'labels', scopeKey(userId)] as const,
  messages: (userId: string | undefined, labelId?: string) => ['gmail', 'messages', scopeKey(userId), labelId || 'all'] as const,
  message: (userId: string | undefined, messageId: string) => ['gmail', 'message', scopeKey(userId), messageId] as const,
  tokenConfig: (userId?: string) => ['gmail', 'tokenConfig', scopeKey(userId)] as const,
};

/**
 * Hook customizado para gerenciamento da integração Gmail com React Query.
 *
 * Fornece métodos para listar labels, obter mensagens, enviar e-mails, e criar rascunhos,
 * com cache inteligente e tratamento de erros.
 *
 * @param userId - ID do usuário (opcional, para multi-tenant)
 */
export const useGmailApi = (userId?: string) => {
  const queryClient = useQueryClient();
  const [selectedLabel, setSelectedLabel] = useState<string | null>('INBOX');

  // Query para listar labels
  const {
    data: labelsData,
    isLoading: isLoadingLabels,
    error: labelsError,
    refetch: refetchLabels
  } = useQuery<ApiResponse<GmailLabelsResponse>>({
    queryKey: gmailQueryKeys.labels(userId),
    queryFn: async () => {
      console.log('[GMAIL API] 🔄 Buscando labels para userId:', userId);
      const response = await gmailApi.getLabels(userId);
      console.log('[GMAIL API] ✅ Labels recebidas:', response.data?.labels?.length || 0);
      console.log('[GMAIL API] 📊 Contagens das labels:', response.data?.labels?.map(l => ({
        id: l.id,
        name: l.name,
        total: l.messagesTotal,
        unread: l.messagesUnread
      })));
      return response;
    },
    enabled: !!userId, // Só executa se tiver userId
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: 2,
  });

  // Query para obter mensagens da label selecionada
  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    error: messagesError,
    refetch: refetchMessages
  } = useQuery<ApiResponse<GmailMessagesResponse>>({
    queryKey: gmailQueryKeys.messages(userId, selectedLabel || undefined),
    queryFn: () => gmailApi.getMessages(selectedLabel || undefined, 50, userId),
    enabled: !!userId && !!selectedLabel,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 2 * 60 * 1000, // 2 minutos
    retry: 2,
  });

  // Query para obter configuração de token
  const {
    data: tokenConfigData,
    isLoading: isLoadingTokenConfig,
    error: tokenConfigError,
    refetch: refetchTokenConfig
  } = useQuery({
    queryKey: gmailQueryKeys.tokenConfig(userId),
    queryFn: () => gmailApi.getTokenConfig(userId),
    enabled: !!userId,
    staleTime: 60 * 1000, // 1 minuto
    gcTime: 5 * 60 * 1000, // 5 minutos
    retry: 2,
  });

  // Mutation para enviar mensagem
  const sendMessageMutation = useMutation({
    mutationFn: (message: GmailSendMessageRequest) =>
      gmailApi.sendMessage(message, userId),
    onSuccess: (response) => {
      if (response.status === 'success') {
        toast.success('E-mail enviado com sucesso!');
        // Invalidar mensagens para refetch
        queryClient.invalidateQueries({ queryKey: gmailQueryKeys.messages(userId, 'SENT') });
        queryClient.invalidateQueries({ queryKey: gmailQueryKeys.messages(userId, selectedLabel || undefined) });
      } else {
        toast.error(response.message || 'Erro ao enviar e-mail');
      }
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Erro ao enviar e-mail');
      toast.error(message);
    },
  });

  // Mutation para criar rascunho
  const createDraftMutation = useMutation({
    mutationFn: (message: GmailSendMessageRequest) =>
      gmailApi.createDraft(message, userId),
    onSuccess: (response) => {
      if (response.status === 'success') {
        toast.success('Rascunho criado com sucesso!');
        // Invalidar mensagens da label DRAFT
        queryClient.invalidateQueries({ queryKey: gmailQueryKeys.messages(userId, 'DRAFT') });
      } else {
        toast.error(response.message || 'Erro ao criar rascunho');
      }
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Erro ao criar rascunho');
      toast.error(message);
    },
  });

  // Mutation para modificar labels de uma mensagem
  const modifyLabelsMutation = useMutation({
    mutationFn: ({ messageId, addLabelIds, removeLabelIds }: {
      messageId: string;
      addLabelIds?: string[];
      removeLabelIds?: string[];
    }) =>
      gmailApi.modifyLabels(messageId, addLabelIds, removeLabelIds, userId),
    onSuccess: (response, variables) => {
      if (response.status === 'success') {
        toast.success('Labels modificadas com sucesso!');
        // Invalidar mensagens para refetch
        queryClient.invalidateQueries({ queryKey: gmailQueryKeys.messages(userId, selectedLabel || undefined) });
        // Invalidar mensagem específica
        queryClient.invalidateQueries({ queryKey: gmailQueryKeys.message(userId, variables.messageId) });
      } else {
        toast.error(response.message || 'Erro ao modificar labels');
      }
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Erro ao modificar labels');
      toast.error(message);
    },
  });

  // Mutation para remover token
  const deleteTokenMutation = useMutation({
    mutationFn: () => gmailApi.deleteTokenConfig(userId),
    onSuccess: (response) => {
      if (response.status === 'success') {
        toast.success('Conta Gmail desconectada com sucesso!');
        // Invalidar todas as queries
        queryClient.invalidateQueries({ queryKey: gmailQueryKeys.all(userId) });
      } else {
        toast.error(response.message || 'Erro ao desconectar conta');
      }
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Erro ao desconectar conta');
      toast.error(message);
    },
  });

  // Função auxiliar para selecionar label
  const selectLabel = useCallback((labelId: string | null) => {
    setSelectedLabel(labelId);
  }, []);

  // Função auxiliar para enviar mensagem
  const sendMessage = useCallback(
    async (message: GmailSendMessageRequest) => {
      if (!message.to || message.to.trim() === '') {
        toast.error('Destinatário não pode estar vazio');
        return;
      }

      if (!message.subject || message.subject.trim() === '') {
        toast.error('Assunto não pode estar vazio');
        return;
      }

      if (!message.body || message.body.trim() === '') {
        toast.error('Mensagem não pode estar vazia');
        return;
      }

      return new Promise<void>((resolve, reject) => {
        sendMessageMutation.mutate(message, {
          onSuccess: () => resolve(),
          onError: (error) => reject(error),
        });
      });
    },
    [sendMessageMutation]
  );

  // Função auxiliar para criar rascunho
  const createDraft = useCallback(
    async (message: GmailSendMessageRequest) => {
      return new Promise<void>((resolve, reject) => {
        createDraftMutation.mutate(message, {
          onSuccess: () => resolve(),
          onError: (error) => reject(error),
        });
      });
    },
    [createDraftMutation]
  );

  // Função auxiliar para modificar labels
  const modifyLabels = useCallback(
    async (messageId: string, addLabelIds?: string[], removeLabelIds?: string[]) => {
      return new Promise<void>((resolve, reject) => {
        modifyLabelsMutation.mutate(
          { messageId, addLabelIds, removeLabelIds },
          {
            onSuccess: () => resolve(),
            onError: (error) => reject(error),
          }
        );
      });
    },
    [modifyLabelsMutation]
  );

  // Função auxiliar para desconectar conta
  const disconnectAccount = useCallback(
    async () => {
      return new Promise<void>((resolve, reject) => {
        deleteTokenMutation.mutate(undefined, {
          onSuccess: () => resolve(),
          onError: (error) => reject(error),
        });
      });
    },
    [deleteTokenMutation]
  );

  // Função para obter mensagem completa
  const useMessage = (messageId: string | null) => {
    return useQuery<ApiResponse<GmailMessage>>({
      queryKey: gmailQueryKeys.message(userId, messageId || ''),
      queryFn: () => gmailApi.getMessage(messageId!, userId),
      enabled: !!userId && !!messageId,
      staleTime: 60 * 1000, // 1 minuto
      gcTime: 5 * 60 * 1000, // 5 minutos
      retry: 2,
    });
  };

  // Extrair dados de forma segura
  const labels = labelsData?.data?.labels || [];
  const messages = messagesData?.data?.messages || [];
  const hasToken = tokenConfigData?.data?.hasToken || false;
  const tokenEmail = tokenConfigData?.data?.email;
  const tokenActive = tokenConfigData?.data?.active;
  const tokenExpired = tokenConfigData?.data?.isExpired;

  return {
    // Dados
    labels,
    messages,
    selectedLabel,
    hasToken,
    tokenEmail,
    tokenActive,
    tokenExpired,

    // Estados de loading
    isLoadingLabels,
    isLoadingMessages,
    isLoadingTokenConfig,
    isSendingMessage: sendMessageMutation.isPending,
    isCreatingDraft: createDraftMutation.isPending,
    isModifyingLabels: modifyLabelsMutation.isPending,
    isDeletingToken: deleteTokenMutation.isPending,

    // Erros
    labelsError,
    messagesError,
    tokenConfigError,
    sendError: sendMessageMutation.error,
    createDraftError: createDraftMutation.error,
    modifyLabelsError: modifyLabelsMutation.error,
    deleteTokenError: deleteTokenMutation.error,

    // Ações
    selectLabel,
    sendMessage,
    createDraft,
    modifyLabels,
    disconnectAccount,
    refetchLabels,
    refetchMessages,
    refetchTokenConfig,
    useMessage,

    // Funções avançadas
    invalidateLabels: () => {
      queryClient.invalidateQueries({ queryKey: gmailQueryKeys.labels(userId) });
    },
    invalidateMessages: (labelId?: string) => {
      queryClient.invalidateQueries({ queryKey: gmailQueryKeys.messages(userId, labelId) });
    },
    invalidateTokenConfig: () => {
      queryClient.invalidateQueries({ queryKey: gmailQueryKeys.tokenConfig(userId) });
    },
    resetSendMutation: sendMessageMutation.reset,
    resetCreateDraftMutation: createDraftMutation.reset,
    resetModifyLabelsMutation: modifyLabelsMutation.reset,
    resetDeleteTokenMutation: deleteTokenMutation.reset,
  };
};

/**
 * Hook separado para health check do serviço Gmail (uso opcional)
 */
export const useGmailHealth = () => {
  return useQuery({
    queryKey: ['gmail', 'health'],
    queryFn: () => gmailApi.health(),
    staleTime: 60 * 1000, // 1 minuto
    gcTime: 5 * 60 * 1000, // 5 minutos
    retry: 1,
  });
};

/**
 * Hook separado para obter URL de autorização OAuth2
 */
export const useGmailOAuth = (userId?: string) => {
  const getAuthUrl = useCallback(async () => {
    try {
      const response = await gmailApi.getOAuthUrl(userId);
      if (response.status === 'success' && response.data?.authUrl) {
        // Redirecionar para URL de autorização
        window.location.href = response.data.authUrl;
      } else {
        toast.error(response.message || 'Erro ao obter URL de autorização');
      }
    } catch (error) {
      const message = getErrorMessage(error, 'Erro ao iniciar autenticação Gmail');
      toast.error(message);
    }
  }, [userId]);

  return {
    getAuthUrl,
  };
};
